import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable, pointerWithin,
} from "@dnd-kit/core";

import ReactCountryFlag from "react-country-flag";

import Loading from "../../components/Loading";
import PdfShower from "../../components/PdfShower";
import { UserContext } from "../../contexts/User.jsx";
import { ACCOUNTING, canAccounting } from "../../helpers/accountingAccess";
import { getFileUrl, getPdfUrl } from "../../core/config/api.js";
import {
  useReconciliation,
  useReconciliationHints,
  uploadStatement,
  deleteStatement,
  reparseStatement,
  autoMap,
  reconcileCost,
  createMatch,
  updateMatch,
  deleteMatch,
  splitLine,
  moveLine,
  searchOrders,
  resolveOrders,
  uploadListingDocument,
  fetchOrderInvoicePdf,
  updateLineDirection,
  voidOrder,
  createHint,
  deleteHint,
} from "../../hooks/dailyRecap/useReconciliation";

import "./reconciliation.scss";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
// gbp_amount is the GBP pivot — £ label is correct under multi-currency
// (line.amount = BankStatementLine.gbp_amount/100 server-side; do NOT switch to the store base symbol).
const gbp = (v) => v == null ? "—" : new Intl.NumberFormat("en-GB",{style:"currency",currency:"GBP",maximumFractionDigits:0}).format(v);

// "2021-06-01" → "1 Jun 2021" (used for the detected statement period).
const fmtDay = (iso) => {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-");
  if (!y || !m || !d) return null;
  return `${Number(d)} ${MONTHS[Number(m) - 1]?.slice(0, 3)} ${y}`;
};
// "1 Jun 2021 – 30 Jun 2021" (collapses to a single date when start == end).
const fmtRange = (start, end) => {
  const a = fmtDay(start), b = fmtDay(end);
  if (a && b) return a === b ? a : `${a} – ${b}`;
  return a || b || null;
};

// Persist the hidden / pushed-up line-id sets across reloads.
const loadIdSet = (key) => {
  try { return new Set(JSON.parse(localStorage.getItem(key) || "[]")); }
  catch { return new Set(); }
};
const saveIdSet = (key, set) => {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch { /* ignore */ }
};

// Columns shown in each bank-line card's mini-table (header / value / AI assessment).
const FIELDS = [
  { key: "name", label: "Name", val: (l) => l.counterparty || "—" },
  { key: "description", label: "Description", val: (l) => l.reference || "—" },
  { key: "date", label: "Date", val: (l) => l.txn_date || "—" },
  { key: "bank_account", label: "Bank Account", val: (l) => l.bank_account || "—" },
  { key: "amount", label: "Amount", val: (l) => gbp(l.amount) },
];

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "purchase", label: "Purchase" },
  { key: "cost", label: "Cost" },
  { key: "revenue", label: "Revenue" },
];

// Card-chip label per side — replaces the old "m/5 Matched" wording (#6).
const KIND_LABEL = { purchase: "Purchase", cost: "Cost", revenue: "Revenue" };

// Import/export position. Only `domestic` owes home-country VAT, so the other
// four are the ones worth a chip on the card.
const ROUTE_LABEL = {
  export: "EXPORT",
  import: "IMPORT",
  outside_scope: "OUTSIDE SCOPE",
  unknown: "UNCLASSIFIED",
};

const HINT_SCOPES = [
  { key: "purchase", label: "Purchase (stock buys)" },
  { key: "cost", label: "Cost (other expenses)" },
  { key: "revenue", label: "Revenue (payments in)" },
  { key: "all", label: "Everything" },
];

/** Which side of the order a debit belongs to: purchase (stock buy) vs cost.
 *  A credit is always revenue. For a debit we decide which recorded cost the
 *  line amount is *closest* to — if that's the "Buy" cost it's a Purchase,
 *  otherwise a generic Cost. The backend's component_label wins when present;
 *  this is the fallback for manual links and pre-label matches. */
const matchKind = (match, line, caseObj) => {
  if (!line) return null;
  if (line.direction === "credit") return "revenue";

  const label = match?.component_label;
  if (label) return label === "Buy" ? "purchase" : "cost";

  const amount = match?.matched_amount ?? line.amount;
  const costs = caseObj?.costs || [];
  if (costs.length && amount != null) {
    // closest recorded cost by absolute difference
    const closest = costs.reduce((best, c) =>
      Math.abs(c.amount - amount) < Math.abs(best.amount - amount) ? c : best);
    return closest.label === "Buy" ? "purchase" : "cost";
  }
  // No recorded costs to compare — fall back to the AI note, else generic cost.
  return /buy/i.test(match?.assessment?.amount?.note || "") ? "purchase" : "cost";
};

/** A full-board-height drop zone covering one half of the board. Dropping a
 *  card on it sets that side's direction ("debit" = left, "credit" = right).
 *  Two of these tile the whole board so a card can be dropped *anywhere* on the
 *  correct side — no dependence on whether the target column has cards there.
 *  pointer-events are off (dnd-kit collision is geometric), so it never blocks
 *  clicks; it only shows a tint while a card from the other side is in flight. */
const DropRail = ({ side, fromDir }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `rail-${side}`, data: { side } });
  const canDrop = !!fromDir && fromDir !== side; // a card from the opposite side
  return (
    <div
      ref={setNodeRef}
      aria-hidden
      className={`rec-rail rec-rail--${side}`
        + (canDrop ? " rec-rail--armed" : "")
        + (isOver && canDrop ? " rec-rail--over" : "")}
    />
  );
};

/** Move a bank line onto a different month's board (period override). The line
 *  keeps its origin month as a "⤺ from <month>" badge. "Reset" clears the move. */
const MoveMonthModal = ({ line, years, onClose, onSave }) => {
  const now = new Date();
  const [year, setYear] = useState(line.period_year || now.getFullYear());
  const [month, setMonth] = useState(line.period_month || (now.getMonth() + 1));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const run = async (y, m) => {
    setSaving(true); setErr(null);
    try { await onSave(y, m); }
    catch (e) { setErr(e?.response?.data?.error || "Couldn't move the line."); setSaving(false); }
  };
  return (
    <div className="rec-modal" onClick={onClose}>
      <div className="rec-modal__box" onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal__title">Move line to a month</div>
        <div className="rec-modal__hint">
          {(line.counterparty || "Bank line")} · {gbp(line.amount)}{line.txn_date ? ` · ${line.txn_date}` : ""}.
          {" "}It will show on the chosen month's board with a "⤺ from {MONTHS[(line.period_month || 1) - 1]?.slice(0, 3)} {line.period_year}" badge.
        </div>
        <div className="rec-move">
          <select className="rec-move__sel" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select className="rec-move__sel" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {err && <div className="rec-split__err">{err}</div>}
        <div className="rec-modal__actions">
          {line.moved && <button className="rec-mini" onClick={() => run(null, null)} disabled={saving}>Reset to origin</button>}
          <button className="rec-mini" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rec-mini rec-mini--ok-revenue" onClick={() => run(year, month)} disabled={saving}>
            {saving ? "Moving…" : "Move"}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Order picker for the split editor — a custom dropdown (not a native <select>)
 *  so each option can show the product thumbnail beside its ref/client label. */
const OrderPicker = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => String(o.id) === String(value ?? ""));
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const thumb = (id) => (id
    ? <img className="rec-split__thumb" src={getFileUrl(id)} alt="" />
    : <span className="rec-split__thumb rec-split__thumb--ph" />);
  return (
    <div className="rec-split__picker" ref={ref}>
      <button type="button" className="rec-split__pickerbtn" onClick={() => setOpen((o) => !o)}>
        {selected
          ? <span className="rec-split__opt">{thumb(selected.image_file_id)}<span className="rec-split__optlabel">{selected.label}</span></span>
          : <span className="rec-split__optph">Select order…</span>}
        <span className="rec-split__caret" aria-hidden>▾</span>
      </button>
      {open && (
        <div className="rec-split__menu">
          {options.length === 0
            ? <div className="rec-split__noresult">No orders.</div>
            : options.map((o) => (
                <button type="button" key={o.id}
                  className={`rec-split__menuitem${String(o.id) === String(value ?? "") ? " rec-split__menuitem--on" : ""}`}
                  onClick={() => { onChange(o.id); setOpen(false); }}>
                  {thumb(o.image_file_id)}<span className="rec-split__optlabel">{o.label}</span>
                </button>
              ))}
        </div>
      )}
    </div>
  );
};

/** Xero-style split editor: allocate one bank line across multiple orders by
 *  amount AND/OR % (either input drives the other). Amounts kept in pence.
 *  Partial allowed (a balance can stay unmapped); over-allocation is blocked. */
const SplitModal = ({ line, cases, existing, onClose, onSave, searchOrders, resolveOrders }) => {
  const totalP = Math.round((line.amount || 0) * 100);
  // Debit lines carry a Purchase/Cost choice per allocation; credits are revenue.
  const isDebit = line.direction === "debit";
  const [rows, setRows] = useState(() =>
    existing.length ? existing : [{ key: "r0", order_id: null, amountP: 0, kind: "purchase" }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  // Orders found via cross-month search (or resolved for existing off-board
  // allocations), merged into the picker so a split can span months.
  const [extraOrders, setExtraOrders] = useState([]);
  const [orderQuery, setOrderQuery] = useState("");
  const [results, setResults] = useState([]); // cross-month search hits to pick from
  const [searching, setSearching] = useState(false);
  const mergeOrders = (prev, more) => {
    const by = {};
    [...prev, ...(more || [])].forEach((o) => { by[o.order_id] = o; });
    return Object.values(by);
  };

  // On open, label any existing allocation whose order isn't on this month's board.
  useEffect(() => {
    const known = new Set(cases.map((c) => c.order_id));
    const unknown = rows.map((r) => r.order_id).filter((id) => id && !known.has(id));
    if (unknown.length && resolveOrders) {
      resolveOrders(unknown).then((os) => setExtraOrders((p) => mergeOrders(p, os))).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced cross-month order search → a clickable results list (below).
  useEffect(() => {
    const q = orderQuery.trim();
    if (!q || !searchOrders) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      searchOrders(q).then((os) => setResults(os || []))
        .catch(() => setResults([])).finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [orderQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const allocP = rows.reduce((s, r) => s + (r.amountP || 0), 0);
  const remP = totalP - allocP;
  const over = remP < 0;
  const chosen = rows.map((r) => r.order_id).filter(Boolean);
  const dup = new Set(chosen).size !== chosen.length;
  const pctOf = (p) => (totalP ? Math.round((p / totalP) * 100) : 0);

  const setAmtP = (i, p) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, amountP: Math.max(0, Math.round(p)) } : r)));
  const setOrder = (i, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, order_id: v } : r)));
  const setKind = (i, k) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, kind: k } : r)));
  const addRow = () => setRows((rs) => [...rs, { key: `r${rs.length}-${Date.now()}`, order_id: null, amountP: Math.max(0, remP), kind: "purchase" }]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, j) => j !== i));

  // Pick a cross-month search result → add it as an allocation (or fill the first
  // empty row), pre-filling the remaining balance. This is how you assign to an
  // order in a different month.
  const addOrderFromSearch = (o) => {
    setExtraOrders((p) => mergeOrders(p, [o])); // so its label resolves in the picker
    setRows((rs) => {
      if (rs.some((r) => r.order_id === o.order_id)) return rs; // already added
      const i = rs.findIndex((r) => !r.order_id);
      if (i >= 0) return rs.map((r, j) => (j === i ? { ...r, order_id: o.order_id } : r));
      const left = Math.max(0, totalP - rs.reduce((s, r) => s + (r.amountP || 0), 0));
      return [...rs, { key: `r${rs.length}-${Date.now()}`, order_id: o.order_id, amountP: left, kind: "purchase" }];
    });
    setOrderQuery(""); setResults([]);
  };

  const labelFor = (o) => `#${o.reference_number}${o.watch_reference ? ` · ${o.watch_reference}` : ""}`
    + `${o.client_name ? ` · ${o.client_name}` : ""}${o.period ? ` · ${o.period}` : ""}`;
  // Current month's orders + any searched/resolved cross-month orders, deduped.
  const orderOptions = mergeOrders(
    cases.map((c) => ({ order_id: c.order_id, reference_number: c.reference_number, watch_reference: c.watch_reference, client_name: c.client_name, stock_image_file_id: c.stock_image_file_id, period: null })),
    extraOrders,
  ).map((o) => ({ id: o.order_id, label: labelFor(o), image_file_id: o.stock_image_file_id }));

  const save = async () => {
    const allocations = rows
      .filter((r) => r.order_id && r.amountP > 0)
      .map((r) => ({ order_id: Number(r.order_id), amount: r.amountP, ...(isDebit ? { kind: r.kind || "purchase" } : {}) }));
    setSaving(true); setErr(null);
    try {
      await onSave(allocations);
    } catch (e) {
      setErr(e?.response?.data?.error || "Couldn't save the split.");
      setSaving(false);
    }
  };

  return (
    <div className="rec-modal" onClick={onClose}>
      <div className="rec-modal__box rec-modal__box--wide" onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal__title">Split across orders</div>
        <div className="rec-modal__hint">
          {(line.counterparty || "Bank line")} · {gbp(line.amount)}{line.txn_date ? ` · ${line.txn_date}` : ""}.
          {" "}Allocate by amount or %, to any number of orders (any month). A balance can be left unmapped.
        </div>
        <div className="rec-split__search">
          <input type="text" className="rec-split__searchinput" value={orderQuery}
            onChange={(e) => setOrderQuery(e.target.value)}
            placeholder="Search any order — ref, watch or client — to add (any month)…" />
          {searching && <span className="rec-split__searching">…</span>}
          {orderQuery.trim() && (
            <div className="rec-split__results">
              {results.length === 0 && !searching
                ? <div className="rec-split__noresult">No matching orders.</div>
                : results.map((o) => (
                    <button type="button" key={o.order_id} className="rec-split__result" onClick={() => addOrderFromSearch(o)}>
                      {o.stock_image_file_id
                        ? <img className="rec-split__thumb" src={getFileUrl(o.stock_image_file_id)} alt="" />
                        : <span className="rec-split__thumb rec-split__thumb--ph" />}
                      <span className="rec-split__optlabel">{labelFor(o)}</span>
                    </button>
                  ))}
            </div>
          )}
        </div>
        <div className="rec-split">
          {rows.map((r, i) => (
            <div className={`rec-split__row${isDebit ? " rec-split__row--debit" : ""}`} key={r.key}>
              <OrderPicker value={r.order_id} options={orderOptions}
                onChange={(id) => setOrder(i, id ? Number(id) : null)} />
              {isDebit && (
                <select className="rec-split__kindsel" value={r.kind || "purchase"} onChange={(e) => setKind(i, e.target.value)} aria-label="Purchase or cost">
                  <option value="purchase">Purchase</option>
                  <option value="cost">Cost</option>
                </select>
              )}
              <div className="rec-split__amt">
                <span className="rec-split__cur">£</span>
                <input type="number" min="0" step="0.01" placeholder="0"
                  value={r.amountP ? r.amountP / 100 : ""}
                  onChange={(e) => setAmtP(i, (parseFloat(e.target.value) || 0) * 100)} />
              </div>
              <div className="rec-split__pct">
                <input type="number" min="0" max="100" step="1" placeholder="0"
                  value={r.amountP ? pctOf(r.amountP) : ""}
                  onChange={(e) => setAmtP(i, totalP * ((parseFloat(e.target.value) || 0) / 100))} />
                <span className="rec-split__pctsign">%</span>
              </div>
              <button className="rec-split__rm" title="Remove this allocation" onClick={() => removeRow(i)}>×</button>
            </div>
          ))}
          <button className="rec-split__add" onClick={addRow}>+ Add order</button>
        </div>
        <div className={`rec-split__foot${over ? " rec-split__foot--over" : ""}`}>
          <span>Allocated <b>{gbp(allocP / 100)}</b> · {pctOf(allocP)}%</span>
          <span>Remaining <b>{gbp(remP / 100)}</b> · {pctOf(remP)}%</span>
        </div>
        {over && <div className="rec-split__err">Allocated exceeds the line total — reduce an amount.</div>}
        {dup && !over && <div className="rec-split__err">An order is selected more than once.</div>}
        {err && <div className="rec-split__err">{err}</div>}
        <div className="rec-modal__actions">
          <button className="rec-mini" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rec-mini rec-mini--ok-revenue" onClick={save} disabled={saving || over || dup}>
            {saving ? "Saving…" : "Save split"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReconciliationBoard = () => {
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);
  const canUpload = canAccounting(user, ACCOUNTING.UPLOAD_STATEMENTS);
  const canVoid = canAccounting(user, ACCOUNTING.VOID_INVOICE);
  const now = new Date();

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [tab, setTab] = useState("overview");
  const [sel, setSel] = useState(null); // { type: 'order' | 'line', id }
  const [busy, setBusy] = useState(false);
  const [parseMsg, setParseMsg] = useState(null);
  // Files dropped/selected, held until the user confirms which period they belong to.
  const [pendingFiles, setPendingFiles] = useState(null); // File[] | null
  const [uploadYear, setUploadYear] = useState(now.getFullYear());
  const [uploadMonth, setUploadMonth] = useState(now.getMonth() + 1);
  const [voidTarget, setVoidTarget] = useState(null); // order case awaiting void confirmation
  const [deleteTarget, setDeleteTarget] = useState(null); // statement awaiting remove confirmation
  const [expanded, setExpanded] = useState(null);     // order_id whose card is flipped to its reverse
  const [teachTarget, setTeachTarget] = useState(null); // { line, kind } → teach-AI popup
  const [splitTarget, setSplitTarget] = useState(null); // line → split-across-orders editor
  const [moveTarget, setMoveTarget] = useState(null); // line → move-to-month picker
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [linkingId, setLinkingId] = useState(null); // 2nd card in a manual link, kept purple while saving
  const [ieOnly, setIeOnly] = useState(false);        // isolate non-domestic orders
  const [pdfPreview, setPdfPreview] = useState(null); // { title, url, loading?, revoke? }
  const [docBusy, setDocBusy] = useState(null);       // listing id currently uploading
  const [dragLineId, setDragLineId] = useState(null); // line being dragged across columns (overview)
  // Start dragging only after an 8px move, so a plain click still selects/links.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  // Lines the user has hidden / pushed up from the unreconciled pool. Persisted
  // so the choices survive a reload; keyed by line id (ids are globally unique).
  const [hidden, setHidden] = useState(() => loadIdSet("rec_hidden"));
  const [promoted, setPromoted] = useState(() => loadIdSet("rec_promoted"));

  const toggleId = (setter, key) => (id) => setter((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    saveIdSet(key, next);
    return next;
  });
  const hideLine = toggleId(setHidden, "rec_hidden");
  const promoteLine = toggleId(setPromoted, "rec_promoted");
  // Sent with every Reconcile Cost / Revenue run so hidden lines are left out of
  // the matching pool (the server has no hidden flag — it lives in this browser).
  const hiddenIds = useMemo(() => [...hidden], [hidden]);

  const { data, isLoading, refetch, isFetching } = useReconciliation(year, month);
  const { data: hintsData } = useReconciliationHints();
  const hints = hintsData?.hints || [];
  const cases = data?.cases || [];
  const lines = data?.lines || [];
  const matches = useMemo(() => (data?.matches || []).filter((m) => m.status !== "rejected"), [data]);

  const years = useMemo(() => [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2], []);

  const lineById = useMemo(() => {
    const m = {};
    lines.forEach((l) => { m[l.id] = l; });
    return m;
  }, [lines]);
  const matchByLine = useMemo(() => {
    const m = {};
    matches.forEach((x) => { m[x.bank_statement_line_id] = x; });
    return m;
  }, [matches]);
  // A line can be SPLIT across several orders → many matches. Keyed list per line
  // (matchByLine keeps only the last; this keeps them all). Used to resolve the
  // right allocation per order row and to compute allocated / remaining.
  const matchesByLine = useMemo(() => {
    const m = {};
    matches.forEach((x) => { (m[x.bank_statement_line_id] ||= []).push(x); });
    return m;
  }, [matches]);
  // { allocated, remaining } in £ for a line, from its confirmed/suggested matches.
  const lineAllocation = (l) => {
    const ms = matchesByLine[l.id] || [];
    const allocated = ms.reduce((s, x) => s + (x.matched_amount || 0), 0);
    return { allocated, remaining: Math.max(0, (l.amount || 0) - allocated), count: ms.length };
  };
  const caseById = useMemo(() => {
    const m = {};
    cases.forEach((c) => { m[c.order_id] = c; });
    return m;
  }, [cases]);

  // What the order's own records say each side SHOULD total to — the "Buy"
  // cost is the Purchase side, every other cost row is the Cost side, and
  // total_received (payment + deposits) is the Revenue side.
  const expectedTotals = (c) => {
    const costs = c.costs || [];
    const purchase = costs.filter((x) => x.label === "Buy").reduce((s, x) => s + x.amount, 0);
    const cost = costs.filter((x) => x.label !== "Buy").reduce((s, x) => s + x.amount, 0);
    return { revenue: c.total_received || 0, purchase, cost };
  };

  // { [orderId]: { purchase, cost, revenue: boolean } } — a side is only "balanced"
  // once its CONFIRMED matches sum to what the order actually expects for that
  // side; one linked line is not enough on its own. Gates the green card colour
  // below so "confirmed" (user linked it) and "balanced" (the total adds up)
  // stay visually distinct — see rec-card--partial in reconciliation.scss.
  const balancedByOrder = useMemo(() => {
    const sums = {};
    matches.forEach((m) => {
      if (m.status !== "confirmed") return;
      const kind = matchKind(m, lineById[m.bank_statement_line_id], caseById[m.order_id]);
      if (!kind) return;
      (sums[m.order_id] ||= {});
      sums[m.order_id][kind] = (sums[m.order_id][kind] || 0) + (m.matched_amount || 0);
    });
    const out = {};
    cases.forEach((c) => {
      const expected = expectedTotals(c);
      const s = sums[c.order_id] || {};
      // Require a nonzero expected total too — a confirmed match against a side
      // the order never recorded anything for should stay flagged, not turn green.
      out[c.order_id] = {
        purchase: expected.purchase > 0 && Math.abs((s.purchase || 0) - expected.purchase) < 0.01,
        cost: expected.cost > 0 && Math.abs((s.cost || 0) - expected.cost) < 0.01,
        revenue: expected.revenue > 0 && Math.abs((s.revenue || 0) - expected.revenue) < 0.01,
      };
    });
    return out;
  }, [matches, lineById, caseById, cases]);

  // One row per order with its matched lines bucketed by side — keeps every
  // board row aligned so an order always faces its own purchase/cost/revenue.
  // Anything that isn't a plain domestic sale — the orders whose VAT position
  // needs a human. `unknown` counts too: an unresolved country is a gap to fill,
  // not a quietly-domestic order.
  const isNonDomestic = (c) => !!c.trade_route && c.trade_route !== "domestic";
  const ieCount = useMemo(() => cases.filter(isNonDomestic).length, [cases]);
  // The backend returns a null route for a jurisdiction it does not model (the
  // margin scheme is UK-only), so the whole feature hides rather than showing a
  // non-UK store controls it can never satisfy.
  const tradeRoutesApply = useMemo(() => cases.some((c) => !!c.trade_route), [cases]);

  const rows = useMemo(() => (ieOnly ? cases.filter(isNonDomestic) : cases).map((c) => {
    const row = { c, revenue: [], purchase: [], cost: [] };
    matches.forEach((m) => {
      if (m.order_id !== c.order_id) return;
      const l = lineById[m.bank_statement_line_id];
      const kind = matchKind(m, l, c);
      if (kind) row[kind].push(l);
    });
    return row;
  }), [cases, matches, lineById, ieOnly]);

  const matchedLineIds = useMemo(() => new Set(matches.map((m) => m.bank_statement_line_id)), [matches]);

  // Partial, case-insensitive search across the line's visible fields.
  const lineMatchesSearch = (l) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [l.counterparty, l.reference, l.txn_date, l.bank_account, gbp(l.amount), String(l.amount)]
      .some((v) => String(v ?? "").toLowerCase().includes(q));
  };

  const unmatchedAll = useMemo(() => lines.filter((l) => !matchedLineIds.has(l.id)), [lines, matchedLineIds]);
  // Lines the user pushed up — shown in their column above the separator, still unmatched.
  const promotedDebits = useMemo(() => unmatchedAll.filter((l) => l.direction === "debit" && promoted.has(l.id)), [unmatchedAll, promoted]);
  const promotedCredits = useMemo(() => unmatchedAll.filter((l) => l.direction === "credit" && promoted.has(l.id)), [unmatchedAll, promoted]);
  // The unreconciled pool: unmatched, not pushed up, (hidden only when toggled on), search-filtered.
  const poolFilter = (l) => !promoted.has(l.id) && (showHidden || !hidden.has(l.id)) && lineMatchesSearch(l);
  const unmatchedDebits = useMemo(() => unmatchedAll.filter((l) => l.direction === "debit" && poolFilter(l)), [unmatchedAll, promoted, hidden, showHidden, search]);
  const unmatchedCredits = useMemo(() => unmatchedAll.filter((l) => l.direction === "credit" && poolFilter(l)), [unmatchedAll, promoted, hidden, showHidden, search]);
  const hiddenCount = useMemo(() => unmatchedAll.filter((l) => hidden.has(l.id) && !promoted.has(l.id)).length, [unmatchedAll, hidden, promoted]);

  // A line from another period (cost paid before the order's month) gets a badge.
  const isCarried = (l) => !!l.carried || (l.period_year && (l.period_year !== year || (l.period_month && l.period_month !== month)));
  const periodLabel = (l) => l.period_month
    ? `${MONTHS[l.period_month - 1]?.slice(0, 3)} ${l.period_year}`
    : `${l.period_year}`;

  // ---- connector geometry ----
  const boardRef = useRef(null);
  const orderRefs = useRef({});
  const lineRefs = useRef({});
  const [paths, setPaths] = useState([]);

  const recompute = () => {
    const board = boardRef.current;
    if (!board) return;
    const b = board.getBoundingClientRect();
    const next = [];
    matches.forEach((m) => {
      // The dragged card carries a live transform, so its measured rect tracks
      // the cursor — skip its connector while in flight so it doesn't draw a
      // long sweep across the board to wherever it's being held.
      if (dragLineId === m.bank_statement_line_id) return;
      const orderEl = orderRefs.current[m.order_id];
      // A split line renders once per allocated order's row; each instance is
      // keyed by line:order so its connector stays SHORT and local to that row
      // (no long lines crossing the board). Fall back to the line-only key for
      // any single-match instance registered before the split.
      const lineEl = lineRefs.current[`${m.bank_statement_line_id}:${m.order_id}`]
        || lineRefs.current[m.bank_statement_line_id];
      const line = lineById[m.bank_statement_line_id];
      // Only draw when BOTH ends are mounted in the live DOM — a card hidden on
      // the current tab leaves a stale ref that would otherwise draw to (0,0).
      if (!orderEl || !lineEl || !line) return;
      if (!document.contains(orderEl) || !document.contains(lineEl)) return;

      const o = orderEl.getBoundingClientRect();
      const ln = lineEl.getBoundingClientRect();
      if ((o.width === 0 && o.height === 0) || (ln.width === 0 && ln.height === 0)) return;
      let x1, y1, x2, y2;
      if (ln.left >= o.right) {
        // line sits right of the order card
        x1 = o.right - b.left; y1 = o.top + o.height / 2 - b.top;
        x2 = ln.left - b.left; y2 = ln.top + ln.height / 2 - b.top;
      } else {
        // line sits left of the order card
        x1 = ln.right - b.left; y1 = ln.top + ln.height / 2 - b.top;
        x2 = o.left - b.left;   y2 = o.top + o.height / 2 - b.top;
      }
      const mx = (x1 + x2) / 2;
      const kind = matchKind(m, line, caseById[m.order_id]);
      next.push({
        id: m.id,
        d: `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`,
        // green connector only when the whole side balances — mirrors the card
        // border rule in LineCard so a link and its connector never disagree.
        status: (m.status === "confirmed" && !balancedByOrder[m.order_id]?.[kind]) ? "partial" : m.status,
        kind,
      });
    });
    setPaths(next);
  };

  useLayoutEffect(() => {
    // refs re-register during render; measure after paint
    const raf = requestAnimationFrame(recompute);
    const onResize = () => requestAnimationFrame(recompute);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, [matches, cases, lines, tab, expanded, dragLineId, balancedByOrder]);

  // ---- actions ----
  // Surface any mutation failure in the banner instead of failing silently.
  const withRefetch = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await refetch();
      // Reconcile status feeds the /pnl Revenue/Purchase/Cost columns — mark its
      // cache stale so an open P&L tab refreshes without a manual reload.
      queryClient.invalidateQueries({ queryKey: ["transactions-pnl"] });
    } catch (e) {
      const status = e?.response?.status;
      setParseMsg(
        status === 401 || status === 403
          ? "Action failed: not authorised — log out and log back in (your session may be for a different environment)."
          : `Action failed${status ? ` (${status})` : ""}: ${e?.response?.data?.error || e?.message || "unknown error"}.`
      );
    } finally {
      setBusy(false);
    }
  };

  // ---- drag a line card to the other column to flip its side (debit ⇄ credit).
  // Works for matched and unmatched lines; the match (if any) stays linked and
  // simply relocates to the correct side after the board refetches.
  const onDragStart = ({ active }) => setDragLineId(active?.data?.current?.lineId ?? null);
  const onDragEnd = ({ active, over }) => {
    setDragLineId(null);
    const from = active?.data?.current?.direction;
    const to = over?.data?.current?.side;
    const lineId = active?.data?.current?.lineId;
    if (!lineId || !to || !from || to === from) return; // dropped on same side / outside
    withRefetch(() => updateLineDirection(lineId, to));
  };

  // Drop / browse → don't upload yet; ask which period the statement covers first.
  const onFilesSelected = (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploadYear(year);
    setUploadMonth(month);
    setPendingFiles(list);
  };

  // Confirmed upload: store every file against the chosen period, then jump the
  // board to that period so the freshly-parsed lines are in view.
  const confirmUpload = async () => {
    const list = pendingFiles || [];
    const uYear = uploadYear, uMonth = uploadMonth;
    setPendingFiles(null);
    await onFiles(list, uYear, uMonth);
    setYear(uYear);
    setMonth(uMonth);
  };

  const onFiles = async (list, uYear, uMonth) => {
    if (!list.length) return;
    setBusy(true);
    try {
      for (let i = 0; i < list.length; i++) {
        setParseMsg(`Parsing ${list[i].name} with AI… (${i + 1}/${list.length})`);
        try {
          const res = await uploadStatement(list[i], uYear, uMonth);
          setParseMsg(res?.statement?.status === "failed"
            ? `${list[i].name}: parse failed — ${res?.statement?.error || "unknown"}`
            : (() => {
                const c = res?.counts;
                if (c) {
                  return `${list[i].name}: ${c.parsed} lines · ${c.new} new`
                    + (c.duplicate ? ` · ${c.duplicate} already uploaded (skipped)` : "");
                }
                return `${list[i].name}: parsed ${res?.statement?.line_count ?? 0} lines.`;
              })());
        } catch (e) {
          setParseMsg(`${list[i].name}: upload failed.`);
        }
      }
      await refetch();
    } finally {
      setBusy(false);
    }
  };

  const confirmVoid = async () => {
    const target = voidTarget;
    if (!target) return;
    setVoidTarget(null);
    setBusy(true);
    try {
      const res = await voidOrder(target.order_id);
      if (res && res.ok === false) setParseMsg(res.error || "Void failed.");
      await refetch();
    } catch (e) {
      setParseMsg(e?.response?.data?.error || "Void failed.");
    } finally {
      setBusy(false);
    }
  };

  // Bidirectional manual connect: pick an order then a line, or the reverse.
  // The second card is flagged via linkingId so it flashes purple (selected)
  // while the match saves, instead of jumping straight to green/orange.
  // Hold both cards in the purple "selected" state briefly before committing,
  // so the second card highlights exactly like the first instead of jumping
  // straight to matched (the refetch relocates it once linked).
  const commitLink = (orderId, lineId, secondCardKey) => {
    setLinkingId(secondCardKey);
    setTimeout(() => {
      withRefetch(() => createMatch(orderId, lineId)).finally(() => { setLinkingId(null); setSel(null); });
    }, 220);
  };
  const onOrderClick = (orderId) => {
    if (sel?.type === "line") {
      commitLink(orderId, sel.id, `order-${orderId}`);
    } else {
      setSel(sel?.type === "order" && sel.id === orderId ? null : { type: "order", id: orderId });
    }
  };
  const onLineClick = (line) => {
    if (matchByLine[line.id]) return; // already matched — unlink first to re-map
    if (sel?.type === "order") {
      commitLink(sel.id, line.id, `line-${line.id}`);
    } else {
      setSel(sel?.type === "line" && sel.id === line.id ? null : { type: "line", id: line.id });
    }
  };

  const refreshHints = () => queryClient.invalidateQueries({ queryKey: ["reconciliation-hints"] });

  // ---- documents (PO / invoice) ----

  // Attach a supplier PO or invoice to the listing without leaving the board;
  // it lands in the listing's "Upload Invoice" tab.
  const onUploadDoc = async (listingId, file) => {
    setDocBusy(listingId);
    setParseMsg(null);
    try {
      await uploadListingDocument(listingId, file);
      await refetch();
      setParseMsg(`Uploaded ${file.name} to the listing.`);
    } catch (e) {
      setParseMsg(e?.response?.data?.error || `Could not upload ${file.name}.`);
    } finally {
      setDocBusy(null);
    }
  };

  // Xero holds no stored copy of the invoice, so this is a live fetch each time
  // — hence a spinner state rather than an instant open like the PO.
  const onViewInvoice = async (c) => {
    setPdfPreview({ title: `Invoice · ${c.invoice_number || ""}`, url: null, loading: true });
    try {
      const url = await fetchOrderInvoicePdf(c.order_id);
      setPdfPreview({ title: `Invoice · ${c.invoice_number || ""}`, url, revoke: true });
    } catch (e) {
      setPdfPreview(null);
      setParseMsg(e?.response?.data?.error || "Could not load the invoice PDF from Xero.");
    }
  };

  // Blob URLs are minted per view, so release them when the modal closes.
  const closePdfPreview = () => {
    setPdfPreview((p) => {
      if (p?.revoke && p.url) URL.revokeObjectURL(p.url);
      return null;
    });
  };

  // ---- cards ----

  const LineCard = ({ l, kind, pool, side = "right", orderId, dragRef, dragListeners, dragAttributes, dragStyle, isDragging }) => {
    const lineMatches = matchesByLine[l.id] || [];
    // In an order's row, show THAT order's allocation; elsewhere (pool/pushed-up)
    // there's no order context, so fall back to the first match (if any).
    const match = orderId != null ? lineMatches.find((m) => m.order_id === orderId) : lineMatches[0];
    // Green (confirmed) is reserved for a side whose CONFIRMED matches total to
    // what the order expects. A confirmed link that doesn't yet complete its side
    // shows as "partial" — solid in the side colour, so it reads as "linked, but
    // this side doesn't add up yet" rather than "done".
    const sideBalanced = !!balancedByOrder[match?.order_id]?.[kind];
    const state = match
      ? (match.status === "confirmed" && !sideBalanced ? "partial" : match.status)
      : null;
    const a = match?.assessment || null;
    const isSplit = lineMatches.length > 1;            // allocated across >1 order
    const alloc = lineAllocation(l);
    const hasMatches = lineMatches.length > 0;
    // Chip now names the side (Purchase / Cost / Revenue) in its not-confirmed
    // colour; the card border + connector convey confirmed (green) vs suggested.
    const statusText = match ? (KIND_LABEL[kind] || "Matched") : "Unmatched";
    const isHidden = hidden.has(l.id);
    const isPromoted = promoted.has(l.id);
    const refKey = `${l.id}:${orderId ?? "pool"}`;
    return (
      <div
        key={l.id}
        ref={(el) => { if (el) lineRefs.current[refKey] = el; else delete lineRefs.current[refKey]; if (dragRef) dragRef(el); }}
        style={dragStyle}
        {...dragListeners}
        {...dragAttributes}
        className={`rec-card rec-card--line rec-card--${kind}${state ? ` rec-card--${state}` : ""}${isHidden ? " rec-card--hidden" : ""}${(sel?.type === "line" && sel.id === l.id) || linkingId === `line-${l.id}` ? " rec-card--selected" : ""}${sel?.type === "order" && !match ? " rec-card--linkable" : ""}${dragListeners ? " rec-card--draggable" : ""}${isDragging ? " rec-card--dragging" : ""}`}
        onClick={() => onLineClick(l)}
      >
        {/* teach ✦ — top-right, no background */}
        {l.direction === "debit" && (
          <button className="rec-star" title="Teach the AI how to recognise this cost"
            onClick={(e) => { e.stopPropagation(); setTeachTarget({ line: l, kind }); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l1.9 5.7L20 9.6l-5 4 1.6 6.4L12 16.6 7.4 20l1.6-6.4-5-4 6.1-1.9z" />
            </svg>
          </button>
        )}
        {/* push-up / hide (pool) · return + split (pushed-up) · split (mapped) —
            outside the card on its column's outer edge */}
        {(pool || isPromoted || hasMatches) && (
          <div className={`rec-sidebtns rec-sidebtns--${side}`}>
            {pool && (
              <>
                <button className="rec-iconbtn" title="Push up to the board (stays unmatched until linked)"
                  onClick={(e) => { e.stopPropagation(); promoteLine(l.id); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
                  </svg>
                </button>
                <button className={`rec-iconbtn${isHidden ? " rec-iconbtn--on" : ""}`} title={isHidden ? "Unhide this line" : "Hide this line"}
                  onClick={(e) => { e.stopPropagation(); hideLine(l.id); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </>
            )}
            {isPromoted && !hasMatches && (
              <button className="rec-iconbtn" title="Return to the unreconciled list"
                onClick={(e) => { e.stopPropagation(); promoteLine(l.id); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" />
                </svg>
              </button>
            )}
            {/* Split this line across multiple orders (by amount or %) */}
            {(isPromoted || hasMatches) && (
              <button className={`rec-iconbtn${isSplit ? " rec-iconbtn--on" : ""}`}
                title={isSplit ? "Edit how this line is split across orders" : "Split this line across multiple orders"}
                onClick={(e) => { e.stopPropagation(); setSplitTarget(l); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="6" y1="3" x2="6" y2="15" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" />
                </svg>
              </button>
            )}
            {/* Move this line to a different month's board */}
            <button className={`rec-iconbtn${l.moved ? " rec-iconbtn--on" : ""}`}
              title={l.moved ? "Moved to this month — change or reset" : "Move this line to another month"}
              onClick={(e) => { e.stopPropagation(); setMoveTarget(l); }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </button>
          </div>
        )}
        <div className="rec-card__top">
          <span className={`rec-status${match ? ` rec-status--${kind}` : " rec-status--none"}`}>
            {statusText}
          </span>
          {isCarried(l) && (
            <span className="rec-period" title={`This bank line is on the ${periodLabel(l)} statement — the cost was paid in a different period.`}>
              ⤺ {periodLabel(l)}
            </span>
          )}
        </div>
        <div className="rec-line__mid">
          <div className="rec-grid">
            {FIELDS.map((f, i) => {
              // A yellow dot beside the column name flags any field the AI did
              // not judge a clean (Exact/Close) match — replaces the verdict row.
              const v = a ? a[f.key] : null;
              const off = a && v && !v.ok && !/exact|close/i.test(v.note || "");
              return (
                <div key={`h-${f.key}`} className={`rec-grid__h${i === FIELDS.length - 1 ? " rec-grid--last" : ""}`}
                  title={v?.note || ""}>
                  {f.label}{off && <i className="rec-dot-off" />}
                </div>
              );
            })}
            {FIELDS.map((f, i) => <div key={`v-${f.key}`} className={`rec-grid__v${i === FIELDS.length - 1 ? " rec-grid--last" : ""}`} title={String(f.val(l))}>{f.val(l)}</div>)}
          </div>
        </div>
        {/* Split / partial allocation: this order's slice of the line + what's left. */}
        {match && (isSplit || alloc.remaining > 0) && (
          <div className="rec-splitinfo" title={isSplit ? "This line is split across multiple orders" : "Part of this line is mapped here"}>
            <span className="rec-splitinfo__alloc">
              {gbp(match.matched_amount)}
              {l.amount ? ` · ${Math.round((match.matched_amount / l.amount) * 100)}%` : ""}
            </span>
            <span className="rec-splitinfo__of"> of {gbp(l.amount)}</span>
            {isSplit && <span className="rec-splitinfo__tag">split ×{alloc.count}</span>}
            {alloc.remaining > 0 && <span className="rec-splitinfo__rem">{gbp(alloc.remaining)} left</span>}
          </div>
        )}
        {(() => {
          // Gemini sometimes returns a bare dash/placeholder as the summary —
          // only render it (single line, just above the actions) when it has text.
          const note = (a?.summary || match?.reason || "").trim();
          const meaningful = note.replace(/[-–—.\s]/g, "").length > 0;
          return meaningful
            ? <div className="rec-summary" title={note}>💡 {note}</div>
            : null;
        })()}
        {match && (
          <div className="rec-card__match">
            {state === "suggested" ? (
              <span className="rec-actions">
                <button className={`rec-mini ${kind === "revenue" ? "rec-mini--ok-revenue-blue" : `rec-mini--ok-${kind}`}`} onClick={(e) => { e.stopPropagation(); withRefetch(() => updateMatch(match.id, "confirm")); }}>Confirm</button>
                <button className="rec-mini" onClick={(e) => { e.stopPropagation(); withRefetch(() => updateMatch(match.id, "reject")); }}>Reject</button>
              </span>
            ) : (
              <button className="rec-mini" onClick={(e) => { e.stopPropagation(); withRefetch(() => deleteMatch(match.id)); }}>Unlink</button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Overview-only: wrap a LineCard so it can be dragged to the opposite column.
  const DraggableLine = (props) => {
    const { l } = props;
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id: `dragline-${l.id}`,
      data: { lineId: l.id, direction: l.direction },
    });
    const dragStyle = transform
      ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 40, position: "relative" }
      : undefined;
    return (
      <LineCard {...props} dragRef={setNodeRef} dragListeners={listeners}
        dragAttributes={attributes} dragStyle={dragStyle} isDragging={isDragging} />
    );
  };

  // Placeholder slot: holds the row position until a real card replaces it.
  const EmptyCard = ({ kind, label }) => (
    <div className={`rec-card rec-card--placeholder rec-card--placeholder-${kind}`}>
      <span className="rec-placeholder__icon">＋</span>
      <span className="rec-placeholder__text">{label}</span>
    </div>
  );

  /**
   * Supplier → destination country flags, shown on every order card.
   *
   * A missing end renders a dashed placeholder rather than being omitted: an
   * unresolved country is the thing a rep needs to notice (it blocks the
   * import/export classification), so it must not look like a tidy blank.
   */
  const TradeRouteFlags = ({ c }) => {
    const route = c.trade_route;
    if (!route) return null;
    const flag = (code, what) =>
      code ? (
        <ReactCountryFlag countryCode={String(code).toUpperCase()} svg
          title={`${what}: ${String(code).toUpperCase()}`} aria-label={String(code).toUpperCase()}
          className="rec-flag" />
      ) : (
        <span className="rec-flag rec-flag--none" title={`${what}: unknown`} />
      );

    return (
      <div className={`rec-route rec-route--${route}`}>
        {flag(c.trade_source, "Bought from")}
        <span className="rec-route__arrow">→</span>
        {flag(c.trade_destination, "Invoiced to")}
        {route !== "domestic" && (
          <span className={`rec-route__chip rec-route__chip--${route}`}>
            {ROUTE_LABEL[route] || route}
          </span>
        )}
        {/* Zero-rating an export is only defensible with proof of removal. An
            export with no shipment or tracking on file is the position that
            actually costs money in an inspection — so it is called out. */}
        {route === "export" && !c.has_export_evidence && (
          <span className="rec-route__chip rec-route__chip--warn" title="Zero-rated as an export, but no shipment or tracking number is on file as proof of removal">
            NO PROOF
          </span>
        )}
      </div>
    );
  };

  const OrderCard = ({ c }) => {
    const open = expanded === c.order_id;
    const stop = (e) => e.stopPropagation();
    const payments = [...(c.components || [])].sort((a, b) => (a.label === "Deposit" ? 0 : 1) - (b.label === "Deposit" ? 0 : 1));
    const clientLabel = [c.client_name, c.client_store_name].filter(Boolean).join(" | ") || "—";
    // Fall back to the summary `po` for a case built before `listings` existed.
    const listings = c.listings?.length ? c.listings : (c.po ? [{ id: null, reference: null, po: c.po, documents: [] }] : []);
    // The ⇄ glyph Alex asked for, drawn as paths rather than the character so it
    // inherits stroke weight and colour from the button and renders identically
    // across platforms — the literal character's shape and baseline vary a lot
    // between system fonts at 14px.
    const flipIcon = (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <line x1="3" y1="9" x2="21" y2="9" />
        <polyline points="17 5 21 9 17 13" />
        <line x1="21" y1="15" x2="3" y2="15" />
        <polyline points="7 11 3 15 7 19" />
      </svg>
    );
    return (
      // The card FLIPS to its reverse instead of dropping a panel below it, so
      // the details never overlay the rows underneath. The wrapper keeps the
      // front's height (the grid must not reflow when a card is turned) and the
      // back face is absolutely positioned, free to run taller and overlay
      // downward the way the old dropdown did.
      <div
        ref={(el) => { if (el) orderRefs.current[c.order_id] = el; else delete orderRefs.current[c.order_id]; }}
        className={`rec-flip${open ? " rec-flip--on" : ""}`}
      >
        <div className="rec-flip__inner">
          <div className="rec-flip__face rec-flip__face--front">
            <div
              className={`rec-card rec-card--case${(sel?.type === "order" && sel.id === c.order_id) || linkingId === `order-${c.order_id}` ? " rec-card--selected" : ""}${sel?.type === "line" ? " rec-card--linkable" : ""}`}
              onClick={() => onOrderClick(c.order_id)}
            >
              {c.is_duplicate && canVoid && (
                <button className="rec-void" title="Duplicate order — void it"
                  onClick={(e) => { e.stopPropagation(); setVoidTarget(c); }}>Void</button>
              )}
              <div className="rec-case__row">
                {c.stock_image_file_id
                  ? <img className="rec-thumb" src={getFileUrl(c.stock_image_file_id)} alt="" />
                  : <span className="rec-thumb rec-thumb--ph" />}
                <div className="rec-card__body">
                  <div className="rec-card__title" title={c.watch_reference || ""}>{c.watch_reference || "—"}</div>
                  {/* Merchant buyers show "Name | Store Name"; retail clients show the
                      name alone (client_store_name is null for them). */}
                  <div className="rec-card__sub" title={`${clientLabel} · #${c.reference_number}${c.invoice_number ? ` · ${c.invoice_number}` : ""}`}>
                    {c.client_name || "—"}
                    {c.client_store_name && <span className="rec-store"> | {c.client_store_name}</span>}
                    {" "}· #{c.reference_number}{c.invoice_number ? ` · ${c.invoice_number}` : ""}
                  </div>
                  {/* Trade route, on EVERY order: where the stock came from
                      (supplier) → where the invoice address sends it. Reads left
                      to right as the movement of goods, which is what decides
                      whether UK VAT applies at all. */}
                  <TradeRouteFlags c={c} />
                </div>
                <div className="rec-card__right">
                  <div className="rec-card__amt">{gbp(c.total_received)}</div>
                  {/* VAT owed to HMRC on this order's watches, labelled with the
                      basis that produced it (the listing's Margin Scheme / VAT
                      toggle). "Mixed" when a multi-watch order spans both. */}
                  {c.vat_owed != null && c.vat_scheme && (
                    <div className="rec-card__vat" title={`${c.vat_scheme === "vat" ? "VAT" : c.vat_scheme === "mixed" ? "Mixed margin scheme / VAT" : "Margin scheme"} owed to HMRC`}>
                      <span className="rec-card__vat-amt">{gbp(c.vat_owed)}</span>
                      <span className="rec-card__vat-label">
                        {c.vat_scheme === "vat" ? "VAT owed to HMRC"
                          : c.vat_scheme === "mixed" ? "Margin / VAT owed to HMRC"
                          : "Margin Scheme owed to HMRC"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button className="rec-flipbtn" title="Turn over — costs, purchase, payments & files"
                onClick={(e) => { e.stopPropagation(); setExpanded(c.order_id); }}>
                {flipIcon}
              </button>
            </div>
          </div>

          {/* Reverse: money OUT (Costs + Purchase) on the left, money IN
              (Payments + Invoice) on the right — mirroring the board's own
              debit-left / credit-right layout. */}
          <div className="rec-flip__face rec-flip__face--back" aria-hidden={!open}>
            <div className="rec-card rec-card--case rec-card--back" onClick={stop}>
              <div className="rec-back__head">
                <span className="rec-back__title" title={c.watch_reference || ""}>
                  {c.watch_reference || "—"} · #{c.reference_number}
                </span>
                <button className="rec-flipbtn rec-flipbtn--back" title="Turn back"
                  onClick={(e) => { e.stopPropagation(); setExpanded(null); }}>
                  {flipIcon}
                </button>
              </div>
              <div className="rec-back__body">
                <div className="rec-panel__cols">
                  <div className="rec-panel__col">
                    <div className="rec-panel__h">Costs</div>
                    {(c.costs || []).length ? (
                      <table className="rec-tbl"><tbody>
                        {c.costs.map((p, i) => (
                          <tr key={i}><td>{p.label}</td><td>{p.date || "—"}</td><td className="rec-tbl__amt">{gbp(p.amount)}</td></tr>
                        ))}
                      </tbody></table>
                    ) : <div className="rec-panel__empty">No costs recorded.</div>}

                    <div className="rec-panel__h">Purchase</div>
                    {listings.length ? listings.map((lst) => (
                      <div className="rec-doc" key={lst.id ?? "summary"}>
                        {/* Only label the watch when there are several — a single-watch
                            order would just repeat the card title. */}
                        {listings.length > 1 && (
                          <div className="rec-doc__ref">{lst.reference || `Listing ${lst.id}`}</div>
                        )}
                        {lst.po ? (
                          <table className="rec-tbl"><tbody>
                            {lst.po.id && <tr><td>PO</td><td colSpan={2} className="rec-po__id" title={lst.po.id}>{lst.po.id}</td></tr>}
                            {lst.po.purchase_date && <tr><td>Purchased</td><td colSpan={2}>{lst.po.purchase_date}</td></tr>}
                            <tr><td>Supplier</td><td colSpan={2}>{lst.po.supplier || "—"}</td></tr>
                            {lst.po.bank && <tr><td>Paid from</td><td colSpan={2}>{lst.po.bank}</td></tr>}
                          </tbody></table>
                        ) : <div className="rec-panel__empty">No purchase order.</div>}

                        <div className="rec-doc__actions">
                          {lst.po?.file_url && (
                            <button className="rec-mini"
                              onClick={() => setPdfPreview({ title: `Purchase order · ${lst.po.id || lst.reference || ""}`, url: lst.po.file_url })}>
                              View PO
                            </button>
                          )}
                          {lst.id && (
                            <label className={`rec-mini rec-mini--upload${docBusy === lst.id ? " rec-mini--busy" : ""}`}>
                              {docBusy === lst.id ? "Uploading…" : "Upload PO / Invoice"}
                              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" hidden
                                disabled={docBusy === lst.id}
                                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUploadDoc(lst.id, f); }} />
                            </label>
                          )}
                        </div>

                        {/* Uploaded paperwork already on the listing's Upload Invoice
                            tab — shown so an upload made here visibly lands. */}
                        {(lst.documents || []).length > 0 && (
                          <ul className="rec-doc__list">
                            {lst.documents.map((d) => (
                              <li key={d.id}>
                                <button className="rec-doc__link" title={d.name}
                                  onClick={() => setPdfPreview({ title: d.name, url: d.pdf_url || d.url })}>
                                  {d.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )) : <div className="rec-panel__empty">No purchase order.</div>}
                  </div>

                  <div className="rec-panel__col">
                    <div className="rec-panel__h">Payments</div>
                    {payments.length ? (
                      <table className="rec-tbl"><tbody>
                        {/* Method (Bank Transfer / Card / Cash / USDT …): a cash or
                            USDT receipt has no bank-statement credit to match. */}
                        {payments.map((p, i) => (
                          <tr key={i}><td>{p.label}{p.method ? ` · ${p.method}` : ""}</td><td>{p.date || "—"}</td><td className="rec-tbl__amt">{gbp(p.amount)}</td></tr>
                        ))}
                      </tbody></table>
                    ) : <div className="rec-panel__empty">No payments recorded.</div>}

                    <div className="rec-panel__h">Invoice</div>
                    {/* Gated on invoice_number, the SAME field the front of the
                        card shows. Gating on xero_invoice_id instead meant an
                        order carrying only `xero_invoice_number` displayed its
                        invoice on the front and "No Xero invoice generated yet."
                        on the back — the card contradicting itself.

                        The two fields are not interchangeable: the PDF is fetched
                        live from Xero and needs the raw id, so when only the
                        number is known the invoice is reported and the button is
                        withheld, rather than claiming there is no invoice. */}
                    {c.invoice_number ? (
                      <div className="rec-doc">
                        <table className="rec-tbl"><tbody>
                          <tr><td>Invoice</td><td colSpan={2}>{c.invoice_number}</td></tr>
                        </tbody></table>
                        {/* Always offered when there is an invoice number. The
                            previous gate on xero_invoice_id was wrong: the live
                            invoicing flow never writes that column — it stamps
                            xero_invoice_number — so the button was withheld on
                            essentially every real order even though the PDF is
                            perfectly fetchable. Xero resolves an invoice by
                            number just as well as by id. */}
                        <div className="rec-doc__actions">
                          <button className="rec-mini" onClick={() => onViewInvoice(c)}>View invoice</button>
                        </div>
                      </div>
                    ) : <div className="rec-panel__empty">No Xero invoice generated yet.</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ---- board layouts ----

  const emptyLabelFor = (k) => k === "purchase" ? "No purchase mapped yet"
    : k === "cost" ? "No cost mapped yet" : "No payment mapped yet";

  // Overview is the only 3-column tab (debits · order · credits). Purchase / Cost /
  // Revenue are 2-column; cross-tab mappings show as small coloured stubs on the
  // order card — debit sides on its left edge, revenue on its right edge.
  const isTriple = tab === "overview";
  const renderOverviewRow = (row) => (
    <div className="rec-row rec-row--triple" key={row.c.order_id}>
      <div className="rec-cell">
        {row.purchase.map((l) => <DraggableLine key={l.id} l={l} kind="purchase" side="right" orderId={row.c.order_id} />)}
        {row.purchase.length === 0 && <EmptyCard kind="purchase" label="No purchase mapped yet" />}
        {row.cost.map((l) => <DraggableLine key={l.id} l={l} kind="cost" side="right" orderId={row.c.order_id} />)}
      </div>
      <div className="rec-cell rec-cell--order"><OrderCard c={row.c} /></div>
      <div className="rec-cell">
        {row.revenue.length
          ? row.revenue.map((l) => <DraggableLine key={l.id} l={l} kind="revenue" side="left" orderId={row.c.order_id} />)
          : <EmptyCard kind="revenue" label="No payment mapped yet" />}
      </div>
    </div>
  );

  const STUB_LABEL = { purchase: "Purchase", cost: "Cost", revenue: "Revenue" };
  // Debits (Purchase/Cost tabs) sit on the LEFT, order on the RIGHT (like Overview);
  // Revenue keeps order LEFT, credits RIGHT.
  const duoLinesLeft = tab === "purchase" || tab === "cost";
  const duoLineSide = duoLinesLeft ? "right" : "left";
  const renderDuoRow = (row) => {
    const kind = tab; // purchase | cost | revenue
    const sideLines = row[kind];
    // Debit-side mappings not shown on this tab → stubs on the order's LEFT edge;
    // a revenue mapping not shown here → stub on the order's RIGHT edge.
    const leftStubs = ["purchase", "cost"].filter((k) => k !== kind && row[k].length > 0);
    const rightStubs = (kind !== "revenue" && row.revenue.length > 0) ? ["revenue"] : [];
    const stub = (k) => (
      <span key={k} className={`rec-stub rec-stub--${k}`}
        title={`${STUB_LABEL[k]} mapped — open the ${STUB_LABEL[k]} tab to see it`} />
    );
    const orderCell = (
      <div className="rec-cell rec-cell--order">
        {leftStubs.length > 0 && <div className="rec-stubs rec-stubs--left">{leftStubs.map(stub)}</div>}
        {rightStubs.length > 0 && <div className="rec-stubs rec-stubs--right">{rightStubs.map(stub)}</div>}
        <OrderCard c={row.c} />
      </div>
    );
    const linesCell = (
      <div className="rec-cell">
        {sideLines.length
          ? sideLines.map((l) => <LineCard key={l.id} l={l} kind={kind} side={duoLineSide} orderId={row.c.order_id} />)
          : <EmptyCard kind={kind} label={emptyLabelFor(kind)} />}
      </div>
    );
    return (
      <div className="rec-row rec-row--duo" key={row.c.order_id}>
        {duoLinesLeft ? <>{linesCell}{orderCell}</> : <>{orderCell}{linesCell}</>}
      </div>
    );
  };

  const unmatchedSide = tab === "revenue" ? unmatchedCredits : unmatchedDebits;
  // Show the Unreconciled section whenever there are unmatched, not-pushed-up
  // lines (so the search box + hidden toggle stay reachable even mid-search).
  const hasPool = unmatchedAll.some((l) => !promoted.has(l.id));

  return (
    <div className="rec-page">
      <div className="rec-topbar">
        <div className="rec-topbar__left">
          <div className="rec-topbar__period">
            <select className="rec-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select className="rec-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            {busy && <span className="rec-muted">Updating…</span>}
          </div>
          {/* uploaded statements live under the period selects, on the left */}
          <StatementChips
            statements={data?.statements || []}
            canUpload={canUpload}
            onDelete={(s) => setDeleteTarget(s)}
            onReparse={(id, name) => {
              setParseMsg(`Re-parsing ${name} with AI…`);
              withRefetch(() => reparseStatement(id)).then(() => setParseMsg(`Re-parsed ${name}.`));
            }}
          />
        </div>

        <div className="rec-topbar__right">
          <StatementDropzone canUpload={canUpload} onFiles={onFilesSelected} />
          <div className="rec-topbar__actions">
            <button className="rec-btn rec-btn--ghost" onClick={() => setLibraryOpen(true)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l1.9 5.7L20 9.6l-5 4 1.6 6.4L12 16.6 7.4 20l1.6-6.4-5-4 6.1-1.9z" />
              </svg>
              AI Hints{hints.length ? ` (${hints.length})` : ""}
            </button>
            {/* hiddenIds: a hidden line is one the user has set aside, so the
                reconcile run must not propose matches for it. */}
            <button className="rec-btn rec-btn--cost" disabled={busy || !lines.length} onClick={() => withRefetch(() => reconcileCost(year, month, hiddenIds))}>
              Reconcile Cost
            </button>
            <button className="rec-btn rec-btn--rev" disabled={busy || !lines.length} onClick={() => withRefetch(() => autoMap(year, month, hiddenIds))}>
              Reconcile Revenue
            </button>
            {/* Unlike the other two this maps nothing — the route is always
                computed. It ISOLATES the non-domestic orders so they can be
                worked through, and reports the count. Toggles off. */}
            {tradeRoutesApply && (
              <button
                className={`rec-btn rec-btn--ie${ieOnly ? " rec-btn--ie-on" : ""}`}
                disabled={busy || !cases.length}
                onClick={() => setIeOnly((v) => !v)}
                title="Show only orders that are not a plain domestic sale"
              >
                Reconcile Import-Export{ieCount ? ` (${ieCount})` : ""}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="rec-tabsbar">
        {TABS.map((t) => (
          <button key={t.key} className={`rec-pagetab${tab === t.key ? " rec-pagetab--on" : ""}`} onClick={() => { setTab(t.key); setExpanded(null); }}>
            {t.label}
          </button>
        ))}
      </div>

      {parseMsg && <div className="rec-banner">{parseMsg}<button className="rec-banner__x" onClick={() => setParseMsg(null)}>×</button></div>}

      {isLoading ? <Loading /> : (
        <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className={`rec-board${isTriple ? " rec-board--triple" : " rec-board--duo"}`} ref={boardRef}>
          {/* Full-height drop zones: while dragging in Overview, the left half
              accepts a card as a debit and the right half as a credit, so a card
              can be dropped anywhere on the correct side. Rendered always (not
              just mid-drag) so dnd-kit measures them up front; they stay invisible
              and click-through until a drag arms them. */}
          {tab === "overview" && (
            <>
              <DropRail side="debit" fromDir={dragLineId ? lineById[dragLineId]?.direction : null} />
              <DropRail side="credit" fromDir={dragLineId ? lineById[dragLineId]?.direction : null} />
            </>
          )}
          <svg className="rec-connectors">
            {paths.map((p) => (
              <path key={p.id} d={p.d} className={`rec-path rec-path--${p.status === "confirmed" ? "conf" : p.status === "partial" ? "part" : "sug"} rec-path--${p.kind}`} />
            ))}
          </svg>

          {isTriple ? (
            <div className="rec-headrow rec-headrow--triple">
              <div>Costs · debits</div>
              <div>Orders ({cases.length})</div>
              <div>Revenue · credits</div>
            </div>
          ) : (
            <div className="rec-headrow rec-headrow--duo">
              {duoLinesLeft ? (
                <>
                  <div>{tab === "purchase" ? "Stock purchases · debits" : "Other costs · debits"}</div>
                  <div>Orders ({cases.length})</div>
                </>
              ) : (
                <>
                  <div>Orders ({cases.length})</div>
                  <div>Revenue · credits</div>
                </>
              )}
            </div>
          )}

          {cases.length === 0 && <div className="rec-empty">No orders this month.</div>}
          {rows.map((row) => isTriple ? renderOverviewRow(row) : renderDuoRow(row))}

          {/* lines the user pushed up — sit in their column, still unmatched */}
          {(promotedDebits.length + promotedCredits.length) > 0 && (
            isTriple ? (
              <div className="rec-row rec-row--triple">
                <div className="rec-cell">{promotedDebits.map((l) => <DraggableLine key={l.id} l={l} kind="cost" side="right" />)}</div>
                <div className="rec-cell" />
                <div className="rec-cell">{promotedCredits.map((l) => <DraggableLine key={l.id} l={l} kind="revenue" side="left" />)}</div>
              </div>
            ) : (
              <div className="rec-row rec-row--duo">
                {duoLinesLeft ? (
                  <>
                    <div className="rec-cell">{promotedDebits.map((l) => <LineCard key={l.id} l={l} kind={tab} side={duoLineSide} />)}</div>
                    <div className="rec-cell" />
                  </>
                ) : (
                  <>
                    <div className="rec-cell" />
                    <div className="rec-cell">{promotedCredits.map((l) => <LineCard key={l.id} l={l} kind="revenue" side="left" />)}</div>
                  </>
                )}
              </div>
            )
          )}

          {hasPool && (
            <div className="rec-unmatchedwrap">
              <div className="rec-sep"><span className="rec-sep__title">Unreconciled Lines</span></div>
              <div className="rec-searchrow">
                <div className="rec-searchbox">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input className="rec-search" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, description, date, bank account or amount…" />
                  {search && <button className="rec-search__x" onClick={() => setSearch("")}>×</button>}
                </div>
                <button type="button"
                  className={`rec-toggle${showHidden ? " rec-toggle--on" : ""}${hiddenCount === 0 ? " rec-toggle--empty" : ""}`}
                  onClick={() => setShowHidden((v) => !v)} aria-pressed={showHidden}
                  title={hiddenCount === 0 ? "Nothing hidden yet — use the ✕ on a line to hide it" : `${hiddenCount} hidden line${hiddenCount === 1 ? "" : "s"}`}>
                  <span className="rec-toggle__track"><span className="rec-toggle__knob" /></span>
                  <span className="rec-toggle__label">Show hidden records ({hiddenCount})</span>
                </button>
              </div>
              <div className="rec-unmatched__hint">Select an order, then a line, to link manually.</div>
              {isTriple ? (
                <div className="rec-row rec-row--triple">
                  <div className="rec-cell">{unmatchedDebits.map((l) => <DraggableLine key={l.id} l={l} kind="cost" pool side="right" />)}</div>
                  <div className="rec-cell" />
                  <div className="rec-cell">{unmatchedCredits.map((l) => <DraggableLine key={l.id} l={l} kind="revenue" pool side="left" />)}</div>
                </div>
              ) : (
                <div className="rec-row rec-row--duo">
                  {duoLinesLeft ? (
                    <>
                      <div className="rec-cell">{unmatchedSide.map((l) => <LineCard key={l.id} l={l} kind={tab} pool side={duoLineSide} />)}</div>
                      <div className="rec-cell" />
                    </>
                  ) : (
                    <>
                      <div className="rec-cell" />
                      <div className="rec-cell">{unmatchedSide.map((l) => <LineCard key={l.id} l={l} kind="revenue" pool side="left" />)}</div>
                    </>
                  )}
                </div>
              )}
              {unmatchedDebits.length + unmatchedCredits.length === 0 && (
                <div className="rec-empty">No lines match your search.</div>
              )}
            </div>
          )}
          {lines.length === 0 && <div className="rec-empty">Upload a statement to begin.</div>}
        </div>
        </DndContext>
      )}

      {pendingFiles && (
        <div className="rec-modal" onClick={() => setPendingFiles(null)}>
          <div className="rec-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="rec-modal__title">Which period does this statement cover?</div>
            <div className="rec-modal__hint">
              {pendingFiles.length === 1
                ? <>Uploading <b>{pendingFiles[0].name}</b>. Pick the month it belongs to — its lines are stored against that period and shown on its page.</>
                : <>Uploading <b>{pendingFiles.length} files</b>. Pick the month they belong to — their lines are stored against that period and shown on its page.</>}
            </div>
            <div className="rec-periodpick">
              <select className="rec-select" value={uploadMonth} onChange={(e) => setUploadMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select className="rec-select" value={uploadYear} onChange={(e) => setUploadYear(Number(e.target.value))}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="rec-modal__actions">
              <button className="rec-mini" onClick={() => setPendingFiles(null)}>Cancel</button>
              <button className="rec-mini rec-mini--ok-revenue" onClick={confirmUpload}>
                Upload to {MONTHS[uploadMonth - 1]} {uploadYear}
              </button>
            </div>
          </div>
        </div>
      )}

      {teachTarget && (
        <div className="rec-modal" onClick={() => setTeachTarget(null)}>
          <div className="rec-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="rec-modal__title">Teach the AI about this cost</div>
            <div className="rec-modal__hint">
              Saved hints are added to the LLM prompt every time you run Reconcile Cost / Revenue —
              they guide the matching without overriding amounts and dates.
            </div>
            <HintForm
              initial={{
                scope: teachTarget.kind === "purchase" ? "purchase" : "cost",
                statement_hint: [
                  teachTarget.line.counterparty ? `Counterparty "${teachTarget.line.counterparty}"` : null,
                  teachTarget.line.reference ? `reference "${teachTarget.line.reference}"` : null,
                  `around ${gbp(teachTarget.line.amount)}`,
                ].filter(Boolean).join(" · "),
              }}
              onSaved={() => { refreshHints(); setTeachTarget(null); setParseMsg("Hint saved — it will be used on the next reconcile run."); }}
              onCancel={() => setTeachTarget(null)}
            />
          </div>
        </div>
      )}

      {libraryOpen && (
        <HintsLibrary
          hints={hints}
          onClose={() => setLibraryOpen(false)}
          onChanged={refreshHints}
        />
      )}

      {splitTarget && (
        <SplitModal
          line={splitTarget}
          cases={cases}
          existing={(matchesByLine[splitTarget.id] || []).map((m) => ({
            key: `m${m.id}`, order_id: m.order_id, amountP: Math.round((m.matched_amount || 0) * 100),
            kind: m.component_label === "Buy" ? "purchase" : "cost",
          }))}
          onClose={() => setSplitTarget(null)}
          searchOrders={searchOrders}
          resolveOrders={resolveOrders}
          onSave={async (allocations) => {
            await splitLine(splitTarget.id, allocations); // throws on 422 → modal shows it
            await refetch();
            queryClient.invalidateQueries({ queryKey: ["transactions-pnl"] });
            setSplitTarget(null);
          }}
        />
      )}

      {moveTarget && (
        <MoveMonthModal
          line={moveTarget}
          years={years}
          onClose={() => setMoveTarget(null)}
          onSave={async (y, m) => {
            await moveLine(moveTarget.id, y, m); // throws → modal shows error
            await refetch();
            setMoveTarget(null);
          }}
        />
      )}

      {/* PO / invoice / uploaded-document preview. Deliberately click-to-open
          rather than hover: the Xero invoice is a live API call per view, so a
          hover preview would fire requests on mouse drift across the board. */}
      {pdfPreview && (
        <div className="rec-modal" onClick={closePdfPreview}>
          <div className="rec-modal__box rec-modal__box--wide rec-modal__box--pdf" onClick={(e) => e.stopPropagation()}>
            <div className="rec-modal__title">{pdfPreview.title}</div>
            <div className="rec-pdf">
              {pdfPreview.loading || !pdfPreview.url
                ? <Loading text="Fetching the PDF from Xero…" type="loading" />
                : <PdfShower fileId={pdfPreview.url} />}
            </div>
            <div className="rec-modal__actions">
              {pdfPreview.url && (
                <a className="rec-mini" href={pdfPreview.url} target="_blank" rel="noopener noreferrer">Open in new tab</a>
              )}
              <button className="rec-mini" onClick={closePdfPreview}>Close</button>
            </div>
          </div>
        </div>
      )}

      {voidTarget && (
        <div className="rec-modal" onClick={() => setVoidTarget(null)}>
          <div className="rec-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="rec-modal__title">Void this order?</div>
            <div className="rec-modal__body">
              This permanently <b>cancels</b> order #{voidTarget.reference_number}
              {voidTarget.client_name ? ` (${voidTarget.client_name})` : ""}, removes its
              payment from Retail Circle, deletes the payment in Xero and <b>voids the Xero
              invoice</b>{voidTarget.invoice_number ? ` (${voidTarget.invoice_number})` : ""}.
              <br /><br />This cannot be undone.
            </div>
            <div className="rec-modal__actions">
              <button className="rec-mini" onClick={() => setVoidTarget(null)}>Cancel</button>
              <button className="rec-mini rec-mini--danger" onClick={confirmVoid}>Void order</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="rec-modal" onClick={() => setDeleteTarget(null)}>
          <div className="rec-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="rec-modal__title">Remove this statement?</div>
            <div className="rec-modal__body">
              Removing <b>{deleteTarget.original_name}</b> deletes its
              {deleteTarget.line_count ? <> <b>{deleteTarget.line_count} parsed line{deleteTarget.line_count === 1 ? "" : "s"}</b></> : " parsed lines"} and
              <b> any reconciliation matches</b> made against those lines (suggested or confirmed).
              <br /><br />This cannot be undone.
            </div>
            <div className="rec-modal__actions">
              <button className="rec-mini" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="rec-mini rec-mini--danger"
                onClick={() => { const id = deleteTarget.id; setDeleteTarget(null); withRefetch(() => deleteStatement(id)); }}>
                Remove statement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Drag-&-drop upload box (PDFs + statement photos). Lives top-right; gated on canUpload. */
const StatementDropzone = ({ canUpload, onFiles }) => {
  const [over, setOver] = useState(false);
  if (!canUpload) return null;
  return (
    <label
      className={`rec-drop${over ? " rec-drop--over" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFiles(e.dataTransfer.files); }}
    >
      <input
        type="file" multiple hidden
        accept=".pdf,.csv,.txt,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.heic"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
      />
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
      </svg>
      <span className="rec-drop__text">
        Drag &amp; drop statements
        <em>PDFs or images · click to browse</em>
      </span>
    </label>
  );
};

/** Compact list of uploaded statements (under the period selects, on the left).
 *  Each is a two-line card: bank | period | line count, then "filename" + reparse + remove.
 *  onDelete receives the whole statement so the parent can confirm before removing. */
const StatementChips = ({ statements, canUpload, onDelete, onReparse }) => {
  if (!statements.length) return null;
  const openStatement = (s) => {
    if (!canUpload || !s.file_id) return;
    // PDFs go to /api/pdfs (raw); images to /api/files (ImageMagick).
    // Statements are FileAccessPolicy-gated and window.open carries no auth
    // header, so prefer the signed URLs the board payload now emits.
    const isPdf = /pdf/i.test(s.mime || "") || /\.pdf$/i.test(s.original_name || "");
    const url = isPdf ? (s.pdf_url ?? getPdfUrl(s.file_id)) : (s.url ?? getFileUrl(s.file_id));
    window.open(url, "_blank", "noopener");
  };
  return (
    <div className="rec-stmts">
      {statements.map((s) => {
        // Line 1: detected bank · period · line count (segments shown only when known).
        const range = fmtRange(s.start_date, s.end_date);
        const segs = [
          s.bank_name,
          range,
          s.status === "parsed" ? `${s.line_count} lines` : s.status,
        ].filter(Boolean);
        return (
          <span key={s.id} className={`rec-stmt rec-stmt--${s.status}`}>
            <span className="rec-stmt__l1">
              {segs.map((seg, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="rec-stmt__sep">|</span>}
                  <span className={i === 0 && s.bank_name ? "rec-stmt__bank" : "rec-stmt__seg"}>{seg}</span>
                </React.Fragment>
              ))}
            </span>
            <span className="rec-stmt__l2">
              {canUpload ? (
                <button
                  className="rec-stmt__name"
                  title={s.error || `Open ${s.original_name} in a new tab`}
                  onClick={() => openStatement(s)}
                >
                  “{s.original_name}”
                </button>
              ) : (
                <span className="rec-stmt__name rec-stmt__name--static" title={s.error || s.original_name}>“{s.original_name}”</span>
              )}
              {canUpload && (
                <button className="rec-stmt__icon" title="Re-parse with AI (recapture full descriptions)"
                  onClick={() => onReparse(s.id, s.original_name)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                </button>
              )}
              {canUpload && (
                <button className="rec-stmt__x" title="Remove" onClick={() => onDelete(s)}>×</button>
              )}
            </span>
          </span>
        );
      })}
    </div>
  );
};

/** Shared add-hint form: used by the teach-AI popup and the library modal. */
const HintForm = ({ initial = {}, onSaved, onCancel }) => {
  const [form, setForm] = useState({ scope: "cost", label: "", statement_hint: "", system_hint: "", note: "", ...initial });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.statement_hint.trim() && !form.system_hint.trim()) {
      setErr("Describe the pattern on the statement or in the system.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await createHint(form);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || "Could not save the hint.");
      setSaving(false);
    }
  };

  return (
    <div className="rec-hintform">
      <div className="rec-hintform__row">
        <label>Applies to
          <select className="rec-select" value={form.scope} onChange={set("scope")}>
            {HINT_SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        {/* Names the rule: titles it in the AI Hints library and is passed to the
            model as a "[Label]" prefix on this hint (see hintsBlock()). */}
        <label>Rule name <span className="rec-opt">(optional)</span>
          <input className="rec-input" value={form.label} onChange={set("label")}
            title="A short name for this rule, so you can find it later in the AI Hints library."
            placeholder="e.g. Supplier wires" />
        </label>
      </div>
      <label>What it looks like on the bank statement
        <textarea className="rec-input" rows={2} value={form.statement_hint} onChange={set("statement_hint")}
          placeholder={'e.g. Debits to "LBJ WATCHES LTD" are stock purchases; the reference holds the invoice number'} />
      </label>
      <label>Where it lives in our system
        <textarea className="rec-input" rows={2} value={form.system_hint} onChange={set("system_hint")}
          placeholder="e.g. Matches the listing's buying price; supplier name is on the purchase order" />
      </label>
      <label>Anything else the AI should know <span className="rec-opt">(optional)</span>
        <textarea className="rec-input" rows={2} value={form.note} onChange={set("note")}
          placeholder="e.g. amounts can differ by a £15 wire fee; payment usually lands 2–3 days later" />
      </label>
      {err && <div className="rec-hintform__err">{err}</div>}
      <div className="rec-modal__actions">
        {onCancel && <button className="rec-mini" onClick={onCancel}>Cancel</button>}
        <button className="rec-mini rec-mini--ok-revenue" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save hint"}</button>
      </div>
    </div>
  );
};

/** Library of saved AI hints (top-right "AI Hints" button), with inline add. */
const HintsLibrary = ({ hints, onClose, onChanged }) => {
  const [adding, setAdding] = useState(false);
  return (
    <div className="rec-modal" onClick={onClose}>
      <div className="rec-modal__box rec-modal__box--wide" onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal__title">AI matching hints</div>
        <div className="rec-modal__hint">
          Everything the AI has been taught about your statements. These are added to the LLM
          prompt on every Reconcile Cost / Revenue run — guidance only, never overriding amounts or dates.
        </div>
        {adding ? (
          <HintForm onSaved={() => { setAdding(false); onChanged(); }} onCancel={() => setAdding(false)} />
        ) : (
          <button className="rec-btn rec-btn--ghost rec-hintlib__add" onClick={() => setAdding(true)}>+ Add hint</button>
        )}
        <div className="rec-hintlib">
          {hints.length === 0 && !adding && (
            <div className="rec-panel__empty">No hints yet — use the ✦ icon on a cost card to teach the AI from a real example.</div>
          )}
          {hints.map((h) => (
            <div key={h.id} className="rec-hintlib__item">
              <span className={`rec-scope rec-scope--${h.scope}`}>{h.scope}</span>
              <div className="rec-hintlib__body">
                {h.label && <div className="rec-hintlib__label">{h.label}</div>}
                {h.statement_hint && <div><span className="rec-hintlib__k">Statement:</span> {h.statement_hint}</div>}
                {h.system_hint && <div><span className="rec-hintlib__k">System:</span> {h.system_hint}</div>}
                {h.note && <div><span className="rec-hintlib__k">Note:</span> {h.note}</div>}
              </div>
              <button className="rec-stmt__x" title="Delete hint"
                onClick={async () => { await deleteHint(h.id); onChanged(); }}>×</button>
            </div>
          ))}
        </div>
        <div className="rec-modal__actions">
          <button className="rec-mini" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// Gate the whole board on the "Reconciliation view" grant
// (master admins pass by default; other roles need the explicit grant).
const ReconciliationPage = () => {
  const { user } = useContext(UserContext);
  if (!canAccounting(user, ACCOUNTING.RECONCILIATION)) {
    return <div className="rec-page"><div className="rec-restricted">Access restricted</div></div>;
  }
  return <ReconciliationBoard />;
};

export default ReconciliationPage;
